import { collection, doc, query, setDoc, where } from "firebase/firestore";
import { useMemo, useState } from "react";
import { useAuth } from "@/auth/AuthProvider";
import { ErrorText, FieldLabel, LoadingView, PrimaryButton, SBCard, SecondaryButton } from "@/components/ui";
import { db } from "@/firebase/app";
import { useCollection, useDoc } from "@/hooks/firestoreHooks";
import { parseLeague } from "@/models/catalog";
import { activeMembership } from "@/models/user";
import { humanActionAuditActor, recordActionAudit } from "@/services/actionAudit";
import {
  athleteValueFirestoreData,
  LEAGUE_ATHLETE_VALUE_STATUS_LABELS,
  parseAthleteValue,
  schemaActiveFields,
  sortAthleteValues,
} from "@/models/schema";
import { useActiveLeagueID } from "@/features/home/useLeagueSnapshot";
import { DynamicValuesEditor, type ScopedValueMaps } from "@/features/competitions/DynamicValuesEditor";
import { parseFieldSchema } from "@/models/schema";
import {
  archivedAthleteValue,
  buildAthleteValue,
  emptyAthleteSchema,
  EMPTY_SCOPED_VALUE_MAPS,
  mapsForAthleteValue,
  validateAthleteMaps,
  visibleAthleteValues,
  type AthleteValueFieldErrors,
} from "./athleteValues";
import "@/features/home/home.css";
import "@/features/league/league.css";

const NEW_ATHLETE_ID = "__new_athlete__";

export function AthleteCreation() {
  const { currentUser } = useAuth();
  const { leagueID } = useActiveLeagueID();
  const membership = currentUser ? activeMembership(currentUser) : undefined;
  const sportID = membership?.sportID ?? currentUser?.activeContext?.sportID;

  const league = useDoc(leagueID ? doc(db, "leagues", leagueID) : null, parseLeague);
  const schemaDoc = useDoc(leagueID ? doc(db, "leagueAthleteSchemas", leagueID) : null, parseFieldSchema);
  const valuesQuery = leagueID ? query(collection(db, "leagueAthleteValues"), where("leagueID", "==", leagueID)) : null;
  const remoteValues = useCollection(valuesQuery, parseAthleteValue, `league-athlete-values:${leagueID}`);

  const schema = useMemo(() => {
    if (!leagueID) return null;
    return schemaDoc.data ?? emptyAthleteSchema(leagueID, sportID);
  }, [leagueID, schemaDoc.data, sportID]);
  const fields = schemaActiveFields(schema);
  const athletes = useMemo(() => [...remoteValues.data].sort(sortAthleteValues), [remoteValues.data]);
  const visibleAthletes = useMemo(() => visibleAthleteValues(athletes), [athletes]);

  const [selectedID, setSelectedID] = useState(NEW_ATHLETE_ID);
  const [name, setName] = useState("");
  const [maps, setMaps] = useState<ScopedValueMaps>(EMPTY_SCOPED_VALUE_MAPS);
  const [fieldErrors, setFieldErrors] = useState<AthleteValueFieldErrors>({});
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const selectedAthlete = visibleAthletes.find((athlete) => athlete.id === selectedID);
  const leagueName = league.data?.name ?? membership?.displayName ?? currentUser?.activeContext?.displayName ?? "League";
  const isLoading = schemaDoc.loading || remoteValues.loading;

  const selectAthlete = (athleteID: string) => {
    setSelectedID(athleteID);
    setFieldErrors({});
    setStatusMessage(null);
    if (athleteID === NEW_ATHLETE_ID) {
      resetForm();
      return;
    }
    const athlete = visibleAthletes.find((entry) => entry.id === athleteID);
    if (!athlete) {
      resetForm();
      setSelectedID(NEW_ATHLETE_ID);
      return;
    }
    setName(athlete.name);
    setMaps(mapsForAthleteValue(athlete, fields));
  };

  const save = async (status: "draft" | "active") => {
    if (!leagueID || !currentUser || !schema) return;
    setIsSaving(true);
    setFieldErrors({});
    setStatusMessage(null);
    try {
      const now = new Date();
      const value = buildAthleteValue({
        leagueID,
        fields,
        existingValue: selectedAthlete,
        explicitName: name,
        maps,
        status,
        userID: currentUser.id,
        now,
      });
      const errors = validateAthleteMaps(
        fields,
        {
          publicValues: value.publicValues,
          organizationValues: value.organizationValues,
          leagueValues: value.leagueValues,
        },
        status === "active",
      );
      if (Object.keys(errors).length > 0) {
        setFieldErrors(errors);
        setStatusMessage("Check the highlighted athlete fields.");
        return;
      }
      await setDoc(doc(db, "leagueAthleteValues", value.id), athleteValueFirestoreData(value));
      void recordActionAudit({
        before: selectedAthlete ? athleteValueFirestoreData(selectedAthlete) : undefined,
        after: athleteValueFirestoreData(value),
        context: {
          leagueID,
          ...(value.organizationID !== undefined && { organizationID: value.organizationID }),
          ...(value.athleteUserID !== undefined && { subjectUserID: value.athleteUserID }),
          targetCollection: "leagueAthleteValues",
          targetDocumentID: value.id,
          targetDisplayName: value.name,
          actionType: selectedAthlete ? "update" : "create",
          actor: humanActionAuditActor(currentUser),
          fieldLabels: actionAuditFieldLabels(fields),
        },
      });
      setSelectedID(value.id);
      setName(value.name);
      setMaps(mapsForAthleteValue(value, fields));
      setStatusMessage(status === "draft" ? "Saved draft." : "Activated athlete.");
    } catch (error) {
      setStatusMessage(error instanceof Error ? error.message : "Athlete could not be saved.");
    } finally {
      setIsSaving(false);
    }
  };

  const archiveSelected = async () => {
    if (!selectedAthlete || !currentUser) return;
    setIsSaving(true);
    setFieldErrors({});
    setStatusMessage(null);
    try {
      const archived = archivedAthleteValue(selectedAthlete, currentUser.id, new Date());
      await setDoc(doc(db, "leagueAthleteValues", archived.id), athleteValueFirestoreData(archived));
      void recordActionAudit({
        before: athleteValueFirestoreData(selectedAthlete),
        after: athleteValueFirestoreData(archived),
        context: {
          leagueID: archived.leagueID,
          ...(archived.organizationID !== undefined && { organizationID: archived.organizationID }),
          ...(archived.athleteUserID !== undefined && { subjectUserID: archived.athleteUserID }),
          targetCollection: "leagueAthleteValues",
          targetDocumentID: archived.id,
          targetDisplayName: archived.name,
          actionType: "archive",
          actor: humanActionAuditActor(currentUser),
          fieldLabels: actionAuditFieldLabels(fields),
        },
      });
      resetForm();
      setSelectedID(NEW_ATHLETE_ID);
      setStatusMessage("Archived athlete.");
    } catch (error) {
      setStatusMessage(error instanceof Error ? error.message : "Athlete could not be archived.");
    } finally {
      setIsSaving(false);
    }
  };

  const resetForm = () => {
    setName("");
    setMaps(EMPTY_SCOPED_VALUE_MAPS);
    setFieldErrors({});
  };

  if (!leagueID) return <ErrorText>Select a league workspace first.</ErrorText>;

  return (
    <div className="list-screen league-screen">
      <div className="list-screen-head">
        <div>
          <h1>Create Athlete</h1>
          <p className="home-hint">Draft or activate league-scoped athlete records for {leagueName}.</p>
        </div>
      </div>

      <SBCard className="home-card">
        <div className="home-card-head">
          <div>
            <h3>Athlete Data</h3>
            <p className="home-hint">
              {fields.length} active {fields.length === 1 ? "field" : "fields"}
            </p>
          </div>
          <span className="badge">{visibleAthletes.length}</span>
        </div>

        {isLoading ? (
          <LoadingView label="Loading athlete data…" />
        ) : (
          <>
            <FieldLabel label="Created athletes">
              <select value={selectedID} onChange={(event) => selectAthlete(event.target.value)} disabled={isSaving}>
                <option value={NEW_ATHLETE_ID}>New</option>
                {visibleAthletes.map((athlete) => (
                  <option key={athlete.id} value={athlete.id}>
                    {athlete.name} ({LEAGUE_ATHLETE_VALUE_STATUS_LABELS[athlete.status]})
                  </option>
                ))}
              </select>
            </FieldLabel>

            {visibleAthletes.length === 0 && <p className="home-hint">Draft and active athletes will appear here.</p>}
            {selectedAthlete && (
              <SecondaryButton title="Archive selected" destructive onClick={() => void archiveSelected()} disabled={isSaving} />
            )}
          </>
        )}
      </SBCard>

      <SBCard className="home-card">
        <div className="home-card-head">
          <h3>Athlete Details</h3>
          {selectedAthlete && <span className="badge">{LEAGUE_ATHLETE_VALUE_STATUS_LABELS[selectedAthlete.status]}</span>}
        </div>

        <FieldLabel label="Athlete name">
          <input value={name} onChange={(event) => setName(event.target.value)} placeholder="Ada Lovelace" disabled={isSaving} />
        </FieldLabel>

        {fields.length === 0 ? (
          <p className="home-hint">Add athlete data fields from Athlete Data configuration to capture profile details.</p>
        ) : (
          <DynamicValuesEditor fields={fields} maps={maps} onChange={setMaps} fieldErrors={fieldErrors} />
        )}
      </SBCard>

      <SBCard className="home-card">
        <div className="league-form-actions">
          <SecondaryButton title={isSaving ? "Saving…" : "Save draft"} onClick={() => void save("draft")} disabled={isSaving} />
          <PrimaryButton title={isSaving ? "Saving…" : "Activate"} onClick={() => void save("active")} disabled={isSaving} />
        </div>
        <ErrorText>
          {schemaDoc.error ?? remoteValues.error ?? league.error ?? (statusMessage?.startsWith("Check") ? statusMessage : null)}
        </ErrorText>
        {statusMessage && !statusMessage.startsWith("Check") && <p className="home-hint">{statusMessage}</p>}
      </SBCard>
    </div>
  );
}

function actionAuditFieldLabels(fields: ReturnType<typeof schemaActiveFields>): Record<string, string> {
  return Object.fromEntries(
    fields.flatMap((field) =>
      ["publicValues", "organizationValues", "leagueValues"].map((prefix) => [`${prefix}.${field.id}`, field.label]),
    ),
  );
}
